import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ref, uploadBytes } from 'firebase/storage';

import { useNavigate, useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import DefaultEventSettingsEditor from '../components/common/DefaultEventSettingsEditor';
import GratiPageWrapper from '../components/common/GratiPageWrapper';
import LeagueGeneralSettingsEditor from '../components/league/LeagueGeneralSettingsEditor';
import LeagueReviewAndFinalize from '../components/league/LeagueReviewAndFinalize';
import { ActionProps, useApp } from '../contextProviders/AppProvider';
import { useData } from '../contextProviders/DataProvider';
import useOrg from '../dataHooks/useOrg';
import { useLeagueMutators } from '../dataMutators/useLeagueMutators';
import { storage } from '../firebase';
import DefaultObjects from '../types/DefaultObjects';
import League from '../types/League';
import { generateFirestoreId } from '../utils/utils';

type Step = 'edit' | 'review';

export default function LeagueCreatePage(): ReactElement {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { setPageName } = useApp();
  const { canCreateLeagues, userState } = useData();
  const { addLeague } = useLeagueMutators();

  const [step, setStep] = useState<Step>('edit');
  const [isEditing, setIsEditing] = useState(false);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [pageActions, setPageActions] = useState<ActionProps[] | null>(null);
  const [showEventDefaults, setShowEventDefaults] = useState(true);

  // Pre-generate the document ID so we can use it for the logo path before saving
  const [docId] = useState(() => generateFirestoreId());

  const initialLeague = useMemo((): League => {
    return {
      ...DefaultObjects.League,
      itemId: docId,
      creatorId: userState.userProfile?.itemId || '',
      isCatchAll: false,
      orgId: orgId ?? '',
    };
  }, [docId, orgId, userState.userProfile?.itemId]);

  const [currentLeague, setCurrentLeague] = useState<League>(initialLeague);

  // Update creatorId if user profile loads later
  useEffect(() => {
    setCurrentLeague((prev) => ({
      ...prev,
      creatorId: initialLeague.creatorId,
    }));
  }, [initialLeague.creatorId]);

  // Fetch org for defaults — use currentLeague.orgId so it updates when user changes org
  const { org, isOrgLoading, orgError } = useOrg(currentLeague.orgId || null);

  // Copy org defaults into league when org changes
  // Only spread defined values — undefined causes Firestore errors with setDoc
  useEffect(() => {
    if (org) {
      setCurrentLeague((prev) => ({
        ...prev,
        orgName: org.shortName || '',
        seasonMonthDays: org.defaultSeasonMonthDays ?? prev.seasonMonthDays,
        ...(org.defaultPlayDays !== undefined && { defaultPlayDays: org.defaultPlayDays }),
        ...(org.defaultEventSignupOpensDaysPrior !== undefined && { defaultEventSignupOpensDaysPrior: org.defaultEventSignupOpensDaysPrior }),
        ...(org.defaultEventSignupClosesDaysPrior !== undefined && { defaultEventSignupClosesDaysPrior: org.defaultEventSignupClosesDaysPrior }),
        ...(org.defaultCoursePair !== undefined && { defaultCoursePair: org.defaultCoursePair }),
        ...(org.defaultStartTime !== undefined && { defaultStartTime: org.defaultStartTime }),
        ...(org.defaultMaxPlayers !== undefined && { defaultMaxPlayers: org.defaultMaxPlayers }),
        ...(org.defaultSignupPriority !== undefined && { defaultSignupPriority: org.defaultSignupPriority }),
        ...(org.defaultGhinIndexSetDaysPrior !== undefined && { defaultGhinIndexSetDaysPrior: org.defaultGhinIndexSetDaysPrior }),
        ...(org.defaultIsTeam !== undefined && { isTeam: org.defaultIsTeam }),
        ...(org.defaultTeamSize !== undefined && { defaultTeamSize: org.defaultTeamSize }),
      }));
    }
  }, [org]);

  useEffect(() => {
    const pageTitles: Record<Step, string> = {
      edit: 'Create league',
      review: 'Review and create',
    };
    setPageName(pageTitles[step]);
  }, [setPageName, step]);

  // Handle browser back button — navigate back to originating page (not just edit step)
  useEffect(() => {
    if (step !== 'review') return;

    window.history.pushState({ step: 'review' }, '');

    const handlePopState = () => {
      // The pushed review entry was already popped by the back button; go back one more to leave the create page
      navigate(-1);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [step, navigate]);

  const handleReview = useCallback(() => {
    setStep('review');
  }, []);

  const handleCreate = useCallback(async () => {
    try {
      // Strip undefined values — Firestore rejects them in setDoc
      let leagueToSave = Object.fromEntries(
        Object.entries(currentLeague).filter(([, v]) => v !== undefined),
      ) as League;

      // If there's a pending logo file, upload it now
      if (pendingLogoFile) {
        const logoRef = ref(
          storage,
          `org/${leagueToSave.orgId}/league/${leagueToSave.itemId}/orgMemberLeagueAdminFile/${leagueToSave.itemId}-logo`,
        );
        const snapshot = await uploadBytes(logoRef, pendingLogoFile);
        leagueToSave = { ...leagueToSave, logo: snapshot.ref.name };
      }

      await addLeague(leagueToSave);
      // Navigate back 2 entries: one for the pushed review step entry, one for the create page
      navigate(-2);
    } catch (error) {
      console.error('Error saving league:', error);
    }
  }, [currentLeague, pendingLogoFile, addLeague, navigate]);

  const handleUpdate = useCallback(<K extends keyof League>(field: K, value: League[K]) => {
    setCurrentLeague((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleLeagueChange = useCallback((league: League) => {
    setCurrentLeague(league);
  }, []);

  const handleOrgChange = useCallback((orgPair: { orgId: string; orgName: string }) => {
    setCurrentLeague((prev) => ({ ...prev, orgId: orgPair.orgId, orgName: orgPair.orgName }));
  }, []);

  const handleImageUpdated = useCallback((snapshot: import('firebase/storage').UploadResult | null) => {
    if (snapshot) {
      setCurrentLeague((prev) => ({ ...prev, logo: snapshot.ref.name }));
    }
  }, []);

  const handleFileSelected = useCallback((file: File | null) => {
    setPendingLogoFile(file);
  }, []);

  const validateUser = () => {
    return canCreateLeagues(orgId) ?? false;
  };

  // Store callbacks in refs to avoid infinite loops in useEffect
  const handleReviewRef = useRef(handleReview);
  const handleCreateRef = useRef(handleCreate);

  useEffect(() => {
    handleReviewRef.current = handleReview;
    handleCreateRef.current = handleCreate;
  });

  // Build page actions based on step
  useEffect(() => {
    switch (step) {
      case 'edit':
        setPageActions([{
          name: 'Review',
          icon: ArrowForwardIcon,
          handleClick: () => handleReviewRef.current(),
          variant: 'contained',
          color: 'primary',
          disabled: !currentLeague.name.trim(),
        }]);
        break;
      case 'review':
        setPageActions([{
          name: 'Create',
          icon: CheckIcon,
          handleClick: () => handleCreateRef.current(),
          variant: 'contained',
          color: 'primary',
        }]);
        break;
    }
  }, [step, currentLeague.name]);

  return (
    <GratiPageWrapper
      validateUser={validateUser}
      isContentLoading={orgId ? isOrgLoading : false}
      contentError={orgError}
      pageActions={pageActions}
    >
      {step === 'edit' && (
        <Box sx={{ pt: 1 }}>
          <Stack spacing={2}>
            <LeagueGeneralSettingsEditor
              league={currentLeague}
              isNew={true}
              onUpdate={handleUpdate}
              onOrgChange={handleOrgChange}
              onFileSelected={handleFileSelected}
            />

            {/* Event Defaults Section */}
            <Paper variant="outlined" sx={{ overflow: 'hidden', py: 0, my: 0 }}>
              <Button
                fullWidth
                variant="text"
                onClick={() => setShowEventDefaults(!showEventDefaults)}
                endIcon={showEventDefaults ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ justifyContent: 'space-between', px: 2 }}
              >
                Event Defaults
              </Button>
              <Collapse in={showEventDefaults}>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      mb: 2
                    }}>
                    These defaults are used when creating new events. Leave blank to inherit from organization.
                  </Typography>
                  <DefaultEventSettingsEditor
                    settings={{
                      defaultPlayDays: currentLeague.defaultPlayDays,
                      defaultEventSignupOpensDaysPrior: currentLeague.defaultEventSignupOpensDaysPrior,
                      defaultEventSignupClosesDaysPrior: currentLeague.defaultEventSignupClosesDaysPrior,
                      defaultCoursePair: currentLeague.defaultCoursePair,
                      defaultStartTime: currentLeague.defaultStartTime,
                      defaultMaxPlayers: currentLeague.defaultMaxPlayers,
                      defaultNumTeamsInMatch: currentLeague.defaultNumTeamsInMatch,
                      defaultSignupPriority: currentLeague.defaultSignupPriority,
                      defaultGhinIndexSetDaysPrior: currentLeague.defaultGhinIndexSetDaysPrior,
                      defaultTeamSize: currentLeague.defaultTeamSize,
                    }}
                    onChange={(field, value) => handleUpdate(field, value as League[typeof field])}
                    parentSettings={{
                      defaultPlayDays: org?.defaultPlayDays,
                      defaultEventSignupOpensDaysPrior: org?.defaultEventSignupOpensDaysPrior,
                      defaultEventSignupClosesDaysPrior: org?.defaultEventSignupClosesDaysPrior,
                      defaultCoursePair: org?.defaultCoursePair,
                      defaultStartTime: org?.defaultStartTime,
                      defaultMaxPlayers: org?.defaultMaxPlayers,
                      defaultNumTeamsInMatch: org?.defaultNumTeamsInMatch,
                      defaultSignupPriority: org?.defaultSignupPriority,
                      defaultGhinIndexSetDaysPrior: org?.defaultGhinIndexSetDaysPrior,
                      defaultTeamSize: org?.defaultTeamSize,
                    }}
                  />
                </Box>
              </Collapse>
            </Paper>
          </Stack>
        </Box>
      )}
      {step === 'review' && (
        <LeagueReviewAndFinalize
          league={currentLeague}
          org={org ?? undefined}
          isNew={true}
          isEditing={isEditing}
          onLeagueChange={handleLeagueChange}
          onEditingClose={() => setIsEditing(false)}
          onEditingOpen={() => setIsEditing(true)}
          onImageUpdated={handleImageUpdated}
          onFileSelected={handleFileSelected}
          onOrgChange={handleOrgChange}
        />
      )}
    </GratiPageWrapper>
  );
}
